// Builds the canonical URL and hreflang alternates for every page at build
// time, so they are part of the SSR HTML instead of being computed in the
// browser. Both come from the same paths Gatsby creates pages at, which keeps
// hreflang hrefs identical to the canonical of the page they point to.

// Front-end base URL (no trailing slash); the same value is the sitemap's siteUrl.
const SITE_URL = (
  process.env.GATSBY_FRONT_END_URL || "https://bravenew.agency"
).replace(/\/+$/, "")
// WordPress (Yoast) builds URLs from the backend host and WP permalinks.
const WP_URL = (
  process.env.GATSBY_WP_URL || "https://super-tajny-headless-cms.bravenew.agency"
).replace(/\/+$/, "")

const DEFAULT_LANGUAGE = "en"

const HREFLANGS = {
  en: "en-GB",
  pl: "pl-PL",
}

const withTrailingSlash = path => (path.endsWith("/") ? path : `${path}/`)

const toAbsoluteUrl = path => `${SITE_URL}${withTrailingSlash(path)}`

const buildSeoUrls = ({ path, language, translations = [] }) => {
  const canonicalUrl = toAbsoluteUrl(path)

  const versions = [
    { language, href: canonicalUrl },
    ...translations
      .filter(t => t && t.path && HREFLANGS[t.language] && t.language !== language)
      .map(t => ({ language: t.language, href: toAbsoluteUrl(t.path) })),
  ]

  // A page without a translation has nothing to point to.
  if (versions.length < 2) {
    return { canonicalUrl, alternates: [] }
  }

  const alternates = versions.map(({ language, href }) => ({
    hreflang: HREFLANGS[language],
    href,
  }))

  const defaultVersion = versions.find(v => v.language === DEFAULT_LANGUAGE)
  if (defaultVersion) {
    alternates.push({ hreflang: "x-default", href: defaultVersion.href })
  }

  return { canonicalUrl, alternates }
}

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// Rewrites URLs in Yoast/ACF JSON-LD to the front end: the backend host
// becomes SITE_URL and the page's WP permalink becomes its canonical URL,
// e.g. a post's /slug/ -> /blog/slug/. Uploaded media keep working under
// SITE_URL thanks to netlify/edge-functions/cms-media.js; other backend
// paths (WP core, themes, plugins) stay on the backend host.
// Returns a string, or the input if it is empty.
const toFrontendUrls = (json, { wpUri, canonicalUrl } = {}) => {
  if (!json) return json

  let text = typeof json === "string" ? json : JSON.stringify(json)
  try {
    // Normalises escaped slashes ("https:\/\/...") written by WP.
    text = JSON.stringify(JSON.parse(text))
  } catch (e) {}

  text = text.replace(
    new RegExp(
      `${escapeRegExp(WP_URL)}(?!/wp/|/app/(?!uploads/|compressx-nextgen/))`,
      "g"
    ),
    SITE_URL
  )

  if (wpUri && canonicalUrl) {
    text = text.split(`${SITE_URL}${wpUri}`).join(canonicalUrl)
  }

  return text
}

// Yoast leaves "item" out of the last breadcrumb (the current page) and
// Google reports it as 'Missing field "item"', so it gets the canonical URL.
const completeBreadcrumbs = (node, canonicalUrl) => {
  if (Array.isArray(node)) return node.forEach(n => completeBreadcrumbs(n, canonicalUrl))
  if (!node || typeof node !== "object") return

  const types = [].concat(node["@type"])
  const items = node.itemListElement
  if (types.includes("BreadcrumbList") && Array.isArray(items) && items.length) {
    const last = items[items.length - 1]
    if (last && typeof last === "object" && !last.item) last.item = canonicalUrl
  }

  Object.values(node).forEach(value => completeBreadcrumbs(value, canonicalUrl))
}

// Yoast's Article author is the WP user who published the post, which is
// not always the author shown on the page (a team member), so the Person
// node the Article points to is replaced with the shown author.
const setArticleAuthor = (schema, author) => {
  const graph = Array.isArray(schema?.["@graph"]) ? schema["@graph"] : null
  if (!graph) return

  const isType = (node, type) => [].concat(node?.["@type"]).includes(type)
  const articles = graph.filter(node => isType(node, "Article"))
  const oldIds = new Set(articles.map(article => article.author?.["@id"]).filter(Boolean))

  const person = {
    "@type": "Person",
    "@id": `${author.url}#person`,
    name: author.name,
    url: author.url,
    ...(author.image && { image: author.image }),
  }

  schema["@graph"] = [
    ...graph.filter(node => !(isType(node, "Person") && oldIds.has(node["@id"]))),
    person,
  ]
  articles.forEach(article => {
    article.author = { name: person.name, "@id": person["@id"] }
  })
}

// JSON-LD from Yoast or ACF, ready to print: front-end URLs, complete
// breadcrumbs and, when given, the author shown on the page ({ name, url,
// image }). Invalid JSON is returned with only the URLs rewritten.
const prepareSchema = (json, pageContext = {}, { author } = {}) => {
  const text = toFrontendUrls(json, pageContext)
  if (!text || !pageContext.canonicalUrl) return text

  try {
    const schema = JSON.parse(text)
    completeBreadcrumbs(schema, pageContext.canonicalUrl)
    if (author?.name && author?.url) {
      setArticleAuthor(schema, {
        ...author,
        image: author.image && toFrontendUrls(author.image),
      })
    }
    return JSON.stringify(schema)
  } catch (e) {
    return text
  }
}

module.exports = { SITE_URL, WP_URL, HREFLANGS, buildSeoUrls, toFrontendUrls, prepareSchema }
